import React, { useState, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';

import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import { validEmail, defaultTeammateMessage, isEmptyString } from 'Shared/helpers';
import { useInviteToWorkspace } from 'Shared/actions/inviteToWorkspace';
import WpButton from 'Wpds/components/button/WpButton';
import TextField from 'Shared/components/TextField/TextField';
import WpAlert from 'Wpds/components/alert/WpAlert';
import TrackingConstants from 'Shared/constants/Tracking.constants';
// Hooks
import useValidateEmployeeEmail from 'Shared/utils/hooks/use-validate-employee-email';

type Props = {
  invitedEmail?: string;
  workspaceId: string;
  workspaceTitle: string;
  isOpen: boolean;
  toggle(): void;
  afterInviteCallback?: Function;
  alertMessage?: string;
};

const parseFullName = fullName => {
  const [firstName, ...lastNameArray] = fullName.split(' ');
  return { firstName, lastName: lastNameArray.join(' '), fullName };
};

const initialWorkspaceNameValue = workspaceTitle => {
  // If workspaceTitle is an email, then throw it out
  return workspaceTitle.includes('@') ? parseFullName('') : parseFullName(workspaceTitle);
};

function InviteTeammateModal({
  invitedEmail = '',
  workspaceId,
  workspaceTitle,
  isOpen,
  toggle,
  afterInviteCallback,
  alertMessage = '',
}: Props) {
  const history = useHistory();
  const { isEmployeeEmailOrValidDomain } = useValidateEmployeeEmail();
  const [workspaceName, setWorkspaceName] = useState(initialWorkspaceNameValue(workspaceTitle));
  const [teammateEmail, setTeammateEmail] = useState(invitedEmail || '');
  const [personalMessage, setPersonalMessage] = useState(defaultTeammateMessage);
  const [errorMessage, setErrorMessage] = useState(null);
  const [inviteToWorkspace, { loading }] = useInviteToWorkspace({
    setFullName: true,
    inviteMethod: TrackingConstants.INVITE_METHOD_TYPES.INVITE_TEAMMATE_TO_ONE_ON_ONE,
  });

  const handleWorkspaceNameChange = useCallback(({ target }) => {
    const { firstName, lastName, fullName } = parseFullName(target.value);
    setWorkspaceName({ firstName, lastName, fullName });
  }, []);

  const handleEmailChange = useCallback(
    event => {
      if (errorMessage) setErrorMessage(null);
      setTeammateEmail(event.target.value);
    },
    [errorMessage]
  );

  const handlePersonalMessageChange = useCallback(event => setPersonalMessage(event.target.value), []);

  const isInvalidEmail = useMemo(() => Boolean(!teammateEmail.length || !validEmail(teammateEmail)), [teammateEmail]);
  const isInvalidAllowedPiDomainEmail = useMemo(
    () => Boolean(!teammateEmail.length || !isEmployeeEmailOrValidDomain({ email: teammateEmail })),
    [teammateEmail]
  );

  const handleSubmit = async () => {
    if (isInvalidEmail) return setErrorMessage(`Invalid email: ${teammateEmail}`);

    const name = `${workspaceName.firstName} ${workspaceName.lastName}`;

    await inviteToWorkspace({
      workspaceId,
      workspaceName: name,
      emails: [teammateEmail],
      personalMessage,
    });
    if (afterInviteCallback) afterInviteCallback();
    return history.push(`/workspaces/${workspaceId}`);
  };

  const isDisabledSubmit =
    isInvalidEmail || isInvalidAllowedPiDomainEmail || loading || isEmptyString(workspaceName.fullName);

  return (
    <WpModal size="sm" isOpen={isOpen} onToggle={toggle}>
      <WpModalHeader>{`Invite ${workspaceName.firstName} ${workspaceName.lastName} to Perform`}</WpModalHeader>
      <WpModalBody>
        {alertMessage && (
          <div className="mb-4">
            <WpAlert type="info">{alertMessage} </WpAlert>
          </div>
        )}
        <div className="d-flex align-items-center mr-1 w-100">
          <TextField
            name="teammateFullName"
            type="text"
            label="Your teammate’s full name"
            className="invite-to-team-modal-field"
            placeholder="First name"
            value={workspaceName.fullName}
            onChange={handleWorkspaceNameChange}
          />
        </div>

        <p className="text-muted">Invite your teammate via email</p>
        <TextField
          name="teammateEmail"
          type="text"
          label="Email"
          className="invite-to-team-modal-field"
          placeholder="Enter your teammate’s work email"
          value={teammateEmail}
          onChange={handleEmailChange}
        />
        {errorMessage && (
          <div className="alert alert-danger p-2 mt-2 mb-0 small w-100" role="alert">
            {errorMessage}
          </div>
        )}

        <div className="d-flex flex-column">
          <div className="small font-weight-bold mb-1">Personal Message</div>
          <textarea
            className="w-100 border rounded p-2"
            value={personalMessage}
            onChange={handlePersonalMessageChange}
          />
        </div>
      </WpModalBody>
      <WpModalFooter>
        <WpButton size="lg" block onClick={handleSubmit} disabled={isDisabledSubmit} loading={loading}>
          Invite
        </WpButton>
      </WpModalFooter>
    </WpModal>
  );
}

export default InviteTeammateModal;
